import { useEffect, useRef, type RefObject } from 'react';

/**
 * Hook to detect clicks outside of a referenced element.
 * Uses a single global event listener shared across all instances.
 *
 * @param handler - Callback to invoke when click outside is detected
 * @param enabled - Whether the listener is active (default: true)
 * @returns A ref to attach to the target element
 *
 * @example
 * ```tsx
 * function Dropdown() {
 *   const [isOpen, setIsOpen] = useState(false);
 *   const dropdownRef = useClickOutside<HTMLDivElement>(() => {
 *     setIsOpen(false);
 *   }, isOpen);
 *
 *   return (
 *     <div ref={dropdownRef}>
 *       {isOpen && <DropdownMenu />}
 *     </div>
 *   );
 * }
 * ```
 */
export function useClickOutside<T extends HTMLElement = HTMLElement>(
  handler: () => void,
  enabled: boolean = true
): RefObject<T | null> {
  const ref = useRef<T | null>(null);
  const savedHandler = useRef(handler);

  // Update handler ref on each render (avoids stale closures)
  useEffect(() => {
    savedHandler.current = handler;
  }, [handler]);

  useEffect(() => {
    if (!enabled) return;

    const listener = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!ref.current || ref.current.contains(target)) {
        return;
      }
      savedHandler.current();
    };

    // Use capture phase to handle events before they reach components
    document.addEventListener('mousedown', listener, true);
    return () => document.removeEventListener('mousedown', listener, true);
  }, [enabled]);

  return ref;
}
